import React, { useRef, useState } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { createAction } from 'redux-actions'

import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Input,
  Row,
  Spinner,
  Table,
  UncontrolledTooltip,
} from 'reactstrap'

import AnimateHelix from 'components/animate_helix'
import { selectAmplicon } from '../reducers/amplicon'
import { updateTaxonomyDropDowns } from '../reducers/taxonomy'
import {
  handleTaxonomySearchString,
  handleSetSelectIndex,
  runTaxonomySearch,
  closeTaxonomySearchModal,
} from '../reducers/taxonomy_search_modal'
import AmpliconFilter from './amplicon_filter'

const LowerRankList = ({ list }) => {
  const [show, setShow] = useState(false)
  const text = show ? 'hide' : 'show'

  if (list.length === 0) {
    return null
  }

  // list contains an array of the remaining taxonomy ranks
  const uniqueList = [...Array.from(new Set(list.map((text) => text[0])).values())]
  const numUniqueItems = uniqueList.length

  // need to have ghost content to preserve the horizontal space taken up by the list
  // but don't want it to reserve any vertical space so the scroll message is not pushed down
  return (
    <div>
      <p>
        <em>
          {numUniqueItems} items{' '}
          <span style={{ cursor: 'pointer' }} onClick={() => setShow(!show)}>
            (<u>{text}</u>)
          </span>
        </em>
      </p>

      {show && (
        <ul style={{ paddingLeft: '16px' }}>
          {uniqueList.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      )}

      <span
        style={{
          color: 'red',
          display: 'inline-block',
          height: 0,
          lineHeight: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          paddingLeft: '16px',
        }}
      >
        <ul style={{ margin: 0 }}>
          {uniqueList.map((item, i) => (
            <li key={`ghost-${i}`}>{item}</li>
          ))}
        </ul>
      </span>
    </div>
  )
}

const loadingStyle = {
  display: 'flex',
  height: '100%',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'absolute',
  top: '40px',
  zIndex: 99999,
} as React.CSSProperties

const stickyStyle = {
  position: 'sticky',
  left: 0,
  backgroundColor: '#eee',
} as React.CSSProperties

const TaxonomySearchModal = (props) => {
  const {
    isOpen,
    isLoading,
    searchStringInput,
    searchString,
    selectIndex,
    results,
    error,
    rankLabelsLookup,
    closeTaxonomySearchModal,
    amplicon,
  } = props

  const inputRef = useRef(null)

  const handleModalOpened = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const rankOrder = ['amplicon', 'taxonomy_source', 'r1', 'r2', 'r3', 'r4', 'r5', 'r6', 'r7', 'r8']
  const rankOrderLookup = Object.fromEntries(rankOrder.map((key, index) => [key, index]))

  const groupByRank = (taxonomies, searchString) => {
    let groups = {}
    for (const taxonomyObj of taxonomies) {
      const taxonomy = taxonomyObj.taxonomy
      const keyArray = [
        taxonomy.amplicon.value,
        taxonomy.taxonomy_source.value,
        taxonomy.r1.value,
        taxonomy.r2.value,
        taxonomy.r3.value,
        taxonomy.r4.value,
        taxonomy.r5.value,
        taxonomy.r6.value,
        taxonomy.r7.value,
        taxonomy.r8.value,
      ]

      let index = keyArray.findIndex((level) =>
        level.toLowerCase().includes(searchString.toLowerCase())
      )
      if (index !== -1) {
        let group = keyArray.slice(0, index + 1).join(',')

        if (!groups[group]) {
          groups[group] = {
            members: [],
            uniqueRanks: {},
            maxIndex: index,
          }
        }

        let lowerLevelRanks = keyArray.slice(index + 1).filter((rank) => rank !== '')

        groups[group].members.push({
          fullObject: taxonomyObj,
          lowerLevelRanks: lowerLevelRanks.length ? [lowerLevelRanks] : [],
        })
      }
    }

    for (let group in groups) {
      const { members, maxIndex } = groups[group]
      const firstTaxonomy = members[0].fullObject.taxonomy
      let allowedKeys = rankOrder.slice(0, maxIndex + 1)

      // start with the groupTaxonomy as keys up to the first sighting of the searchString
      let groupTaxonomy = Object.fromEntries(
        Object.entries(firstTaxonomy).filter(([key]) => allowedKeys.includes(key))
      )

      // if all members share the same next rank, absorb that too, process recursively
      // note, this always will apply to a situation where there is only 1 member, which is when 2 or more consecutive ranks match the searchString
      let canAbsorb = true
      while (canAbsorb) {
        const currentIndex = groups[group].maxIndex
        const nextRank = rankOrder[currentIndex + 1]

        if (!nextRank) break // no more ranks left

        const firstTax = members[0].fullObject.taxonomy

        // skip if first taxonomy is missing the next rank
        if (!firstTax || !firstTax[nextRank] || !firstTax[nextRank].value) break
        const firstValue = firstTax[nextRank].value

        canAbsorb = members.every((m) => {
          const tax = m.fullObject.taxonomy
          return tax && tax[nextRank] && tax[nextRank].value === firstValue
        })

        if (canAbsorb) {
          // absorb the rank and up the maxIndex
          groupTaxonomy[nextRank] = members[0].fullObject.taxonomy[nextRank]
          groups[group].maxIndex += 1

          // update the members
          members.forEach((member) => {
            if (member.lowerLevelRanks.length > 0 && member.lowerLevelRanks[0].length > 0) {
              // remove first rank, if no ranks left set empty array so no bullet point list
              member.lowerLevelRanks[0].shift()
              if (member.lowerLevelRanks[0].length === 0) {
                member.lowerLevelRanks = []
              }
            }
          })
        }
      }

      groups[group].uniqueRanks = groupTaxonomy
    }

    return groups
  }

  let resultsAdjusted
  if (results.length) {
    const groups = groupByRank(results, searchString)
    resultsAdjusted = Object.values(groups).map((group: any) => {
      return {
        taxonomy: group.uniqueRanks,
        list: group.members.map((m) => m.lowerLevelRanks).flat(),
        maxIndex: group.maxIndex,
      }
    })
  } else {
    resultsAdjusted = []
  }

  // uses the same mechanism when clicking on a slice of the taxonomy pie chart in Interactive Graph Search
  const setTaxonomy = (taxonomy, index) => {
    props.handleSetSelectIndex(index)
    const currentAmplicon = props.amplicon
    const currentTaxonomy = props.taxonomy

    // check to see if any ranks are present in the selected taxonomy before calling selectTaxonomyValue
    if (taxonomy.amplicon) {
      // if amplicon value is already the same, using selectAmplicon again messes with dropdowns
      if (currentAmplicon.value !== taxonomy.amplicon.id) {
        props.selectAmplicon(taxonomy.amplicon.id)
      }
    }
    if (taxonomy.taxonomy_source) {
      if (currentTaxonomy.taxonomy_source.selected.value !== taxonomy.taxonomy_source.id) {
        // need to use updateTaxonomyDropDown if taxonomy source has changed
        props.selectTaxonomyValue('taxonomy_source', taxonomy.taxonomy_source.id)
        props.updateTaxonomyDropDown('taxonomy_source')
      }
    }
    if (taxonomy.r1) {
      if (currentTaxonomy.r1.selected.value !== taxonomy.r1.id) {
        props.selectTaxonomyValue('r1', taxonomy.r1.id)
      }
    }
    if (taxonomy.r2) {
      if (currentTaxonomy.r2.selected.value !== taxonomy.r2.id) {
        props.selectTaxonomyValue('r2', taxonomy.r2.id)
      }
    }
    if (taxonomy.r3) {
      if (currentTaxonomy.r3.selected.value !== taxonomy.r3.id) {
        props.selectTaxonomyValue('r3', taxonomy.r3.id)
      }
    }
    if (taxonomy.r4) {
      if (currentTaxonomy.r4.selected.value !== taxonomy.r4.id) {
        props.selectTaxonomyValue('r4', taxonomy.r4.id)
      }
    }
    if (taxonomy.r5) {
      if (currentTaxonomy.r5.selected.value !== taxonomy.r5.id) {
        props.selectTaxonomyValue('r5', taxonomy.r5.id)
      }
    }
    if (taxonomy.r6) {
      if (currentTaxonomy.r6.selected.value !== taxonomy.r6.id) {
        props.selectTaxonomyValue('r6', taxonomy.r6.id)
      }
    }
    if (taxonomy.r7) {
      if (currentTaxonomy.r7.selected.value !== taxonomy.r7.id) {
        props.selectTaxonomyValue('r7', taxonomy.r7.id)
      }
    }
    if (taxonomy.r8) {
      if (currentTaxonomy.r7.selected.value !== taxonomy.r7.id) {
        props.selectTaxonomyValue('r8', taxonomy.r8.id)
      }
    }

    // just short delay before closing
    // apparently looks cool seeing some of the dropdowns change magically
    setTimeout(() => {
      closeTaxonomySearchModal()
    }, 300)
  }

  const onSubmit = () => {
    if (isLoading) {
      return
    }

    if (searchStringInput) {
      props.runTaxonomySearch()
    }
  }

  const RankCell = ({ taxonomy, rank, list, maxIndex, index }) => {
    const id = `rankCell-${index}-${rank}`
    return (
      <>
        <span id={id}>{taxonomy[rank] && taxonomy[rank].value}</span>
        {maxIndex === rankOrderLookup[rank] - 1 && <LowerRankList list={list} />}
        <RankLabel {...{ id, taxonomy, rank }} />
      </>
    )
  }

  const RankLabel = ({ id, taxonomy, rank }) => {
    const thisRankOrder = rankOrderLookup[rank]
    const thisRankIndex = thisRankOrder - 2

    if (taxonomy[rank]) {
      return (
        <UncontrolledTooltip target={id} placement="auto">
          {rankLabelsLookup[taxonomy.taxonomy_source.id][thisRankIndex]}
        </UncontrolledTooltip>
      )
    } else {
      return null
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      data-tut="reactour__TaxonomySearchModal"
      id="reactour__TaxonomySearchModal"
      contentClassName="modalContentStyle"
      onOpened={handleModalOpened}
    >
      <ModalHeader
        toggle={closeTaxonomySearchModal}
        data-tut="reactour__CloseTaxonomySearchModal"
        id="CloseTaxonomySearchModal"
      >
        Taxonomy Search
      </ModalHeader>
      <ModalBody style={{ overflowY: 'scroll' }}>
        <Row style={{ margin: 4, width: '900px' }}>
          <p>
            The Taxonomy Search tool will search each taxonomy source for the given amplicon in the
            Australian Microbiome database for occurances of a given search string. Use this tool if
            you are unsure which taxonomy source a particular taxonomic rank of interest belongs to.
          </p>
          <p>
            Results will be grouped at the highest common rank containing the search string (case
            insensitive). Note that the rank labels change depending on the taxonomy source. The
            Australian Microbiome database uses a convention that preceedes each rank with a
            lowercase abbreviation of the label before the value, e.g. "p__" for Phylum, "sg__" for
            Supergroup. The rank label associated with a given taxonomy can be also seen by hovering
            over the value.
          </p>
          <p>
            When viewing the search results, click the "Select" button to set the taxonomy search
            filters for that taxonomy.
          </p>
        </Row>
        <Row style={{ margin: 4 }}>
          <Col>
            <div
              style={{ display: 'flex', width: '400px', alignItems: 'center', marginBottom: '8px' }}
            >
              <div style={{ width: '100px' }}>
                <strong>Amplicon:</strong>
              </div>
              <div style={{ width: '300px' }}>
                <AmpliconFilter selectBoxOnly={true} keepExistingValue={true} />
              </div>
            </div>
          </Col>
        </Row>
        <Row style={{ margin: 1 }}>
          <Col>
            <Input
              innerRef={inputRef}
              name="searchStringInput"
              id="searchStringInput"
              width="250px"
              value={searchStringInput}
              placeholder="e.g. Skeletonema"
              disabled={isLoading}
              onChange={(evt) => props.handleTaxonomySearchString(evt.target.value)}
              onKeyDown={(evt) => {
                if (evt.key === 'Enter') {
                  onSubmit()
                }
              }}
            />
          </Col>
          <Col>
            <Button color="warning" disabled={isLoading} onClick={onSubmit}>
              Taxonomy Search
            </Button>
          </Col>
        </Row>
        <Col style={{ marginTop: 10, marginBottom: 10 }}>
          {isLoading && (
            <div style={loadingStyle}>
              <AnimateHelix />
            </div>
          )}
          {searchString && (
            <div style={{ margin: 1 }}>
              {resultsAdjusted.length > 0 ? (
                <p>Search results for: {searchString}</p>
              ) : (
                <p>No results for: {searchString}</p>
              )}
            </div>
          )}
        </Col>
        {error && (
          <Col>
            <div>
              <p>{error}</p>
            </div>
          </Col>
        )}
        {resultsAdjusted.length > 0 && (
          <Col>
            <div style={{ overflowX: 'auto' }}>
              <Table>
                <thead>
                  <tr>
                    <th scope="row" style={stickyStyle}></th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <th key={index}>
                        <Button
                          onClick={() => setTaxonomy(taxonomy, index)}
                          disabled={selectIndex && selectIndex !== index}
                          style={{ width: '80px' }}
                        >
                          {selectIndex == index ? <Spinner size="sm"></Spinner> : 'Select'}
                        </Button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Amplicon
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>{taxonomy.amplicon && taxonomy.amplicon.value}</td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Taxonomy Source
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        {taxonomy.taxonomy_source && taxonomy.taxonomy_source.value}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 1
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r1" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 2
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r2" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 3
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r3" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 4
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r4" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 5
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r5" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 6
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r6" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 7
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r7" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                  <tr>
                    <th scope="row" style={stickyStyle}>
                      Rank 8
                    </th>
                    {resultsAdjusted.map(({ taxonomy, list, maxIndex }, index) => (
                      <td key={index}>
                        <RankCell rank="r8" {...{ taxonomy, list, maxIndex, index }} />
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
            </div>
            <div>
              <p>
                <em>
                  Scroll sideways to see all <strong>{resultsAdjusted.length}</strong> results
                </em>
              </p>
            </div>
          </Col>
        )}
      </ModalBody>
    </Modal>
  )
}

function mapStateToProps(state) {
  const { isOpen, isLoading, searchStringInput, searchString, selectIndex, results, error } =
    state.searchPage.taxonomySearchModal

  const selectedAmplicon = state.searchPage.filters.selectedAmplicon
  const ampliconNames = state.referenceData.amplicons
  const ampliconLookup = ampliconNames.values.find((x) => x.id === selectedAmplicon.value)
  const ampliconName = ampliconLookup && ampliconLookup.value

  return {
    isOpen,
    isLoading,
    searchStringInput,
    searchString,
    selectIndex,
    results,
    error,
    amplicon: { ...selectedAmplicon, ...{ text: ampliconName } },
    taxonomy: state.searchPage.filters.taxonomy,
    rankLabelsLookup: state.referenceData.ranks.rankLabelsLookup,
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      handleTaxonomySearchString,
      handleSetSelectIndex,
      runTaxonomySearch,
      closeTaxonomySearchModal,
      selectAmplicon,
      selectTaxonomyValue: (taxa, id) => createAction('SELECT_' + taxa.toUpperCase())(id),
      updateTaxonomyDropDown: (taxa) => updateTaxonomyDropDowns(taxa)(),
    },
    dispatch
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(TaxonomySearchModal)
